/**
 * 供应商配置（Alice 页面 providers 的复刻）：左侧供应商列表，右侧详情（健康概览、熔断策略、Key、Logo、可用模型、删除）。
 *
 * 背景：供应商在本仓叫「上游渠道」（channels 表）。路由、渠道模型库都挂在渠道下；渠道 Key 用根密钥加密存储，
 *   接口只返回末 4 位。健康由数据面按连续失败自动熔断，冷却后由探活恢复。
 * 设计意图：
 *   - 版式与 Alice 一致：.providers-layout 左右两栏、.provider-health-hero 五项指标、.config-card 熔断策略。
 *   - 旧页面的能力全部保留：探活与手动恢复在详情头部，节点参数（drop_params、HTTP/2、max_completion_tokens、
 *     reasoning_content、同渠道重试状态码、流式 usage、去 prompt_cache_key）在「完整设置」弹窗里编辑（ChannelForm）。
 *   - 「新增供应商」先打开预置目录（PresetPicker，照 Asterove Gateway）：选一家填 Key，后端探测通过就建渠道、启用并登记
 *     上游模型清单；目录里没有的再走「自定义供应商」进完整表单。
 *   - 本仓没有的：Alice 的「显示/复制完整 Key」（Key 加密存储，接口只返回末 4 位），所以只显示末 4 位与轮换。
 * 关键约束：只有 admin 能写；Key 输入框用 type="text"（仓库约定），提交后立即清空，不在页面留存；
 *   删除渠道在仍被路由或渠道模型库引用时由后端返回 409，页面显示引用数。
 */
import { IconHeartRateMonitor, IconPlugConnected, IconPlus, IconSettings, IconStethoscope, IconTrash } from '@tabler/icons-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { catalogApi, type CatalogChannel, type ChannelStat, type FetchedModel } from '@ap/api-client/admin-catalog'
import type { ProbeResult } from '@ap/api-client/types'
import { AG_catalog as G, errorTextCat } from '@ap/i18n/admin-catalog'
import { C } from '@ap/i18n/common'
import { useI18n } from '@ap/i18n'
import { ConfirmModal, Notice, fmtDateTime, useLoad, useToast } from '@ap/ui'
import { IosToggle, Logo, SearchBox, fmtContext, fmtMs, pct } from '../alice/kit-catalog.tsx'
import { useAdmin } from '../session.tsx'
import { ChannelForm } from './catalog/ChannelForm.tsx'
import { PresetPicker } from './catalog/PresetPicker.tsx'

type Health = 'healthy' | 'degraded' | 'open' | 'off'

/** 健康状态：停用 → off；自动熔断 → open；有连续失败但还没熔断 → degraded；其余 healthy。 */
function healthOf(c: CatalogChannel): Health {
  if (!c.enabled) return 'off'
  if (c.health_status === 'auto_disabled') return 'open'
  return c.consecutive_failures > 0 ? 'degraded' : 'healthy'
}

export function Channels() {
  const { t, locale } = useI18n()
  const toast = useToast()
  const { isAdmin } = useAdmin()
  const list = useLoad(() => catalogApi.channels(), [])
  const stats = useLoad(() => catalogApi.channelsOverview(24), [])
  const [selected, setSelected] = useState('')
  const [search, setSearch] = useState('')
  const [editing, setEditing] = useState<CatalogChannel | 'new' | null>(null)
  const [picking, setPicking] = useState(false)
  const [deleting, setDeleting] = useState<CatalogChannel | null>(null)
  const [busy, setBusy] = useState('')
  const [probes, setProbes] = useState<Record<string, ProbeResult>>({})
  const [fetched, setFetched] = useState<Record<string, FetchedModel[]>>({})
  const [delError, setDelError] = useState('')

  const channels = list.data?.data ?? []
  const current = channels.find((c) => c.id === selected) ?? null
  const shown = useMemo(() => {
    const q = search.trim().toLowerCase()
    return channels.filter((c) => !q || c.id.toLowerCase().includes(q) || c.name.toLowerCase().includes(q) || c.base_url.toLowerCase().includes(q))
  }, [channels, search])

  // 默认选中第一个供应商；被删除后回到第一个。刚从目录装好的渠道要等列表刷新后才出现，这期间保留选中。
  const pendingId = useRef('')
  useEffect(() => {
    if (!channels.length) return
    if (channels.some((c) => c.id === selected)) {
      if (pendingId.current === selected) pendingId.current = ''
    } else if (pendingId.current !== selected) {
      setSelected(channels[0].id)
    }
  }, [channels, selected])

  const reloadAll = () => {
    list.reload()
    stats.reload()
  }

  const run = async (key: string, fn: () => Promise<void>) => {
    setBusy(key)
    try {
      await fn()
    } catch (e) {
      console.error('[providers] 操作失败', key, e)
      toast.error(e)
    } finally {
      setBusy('')
    }
  }

  const probe = (c: CatalogChannel) =>
    run('test', async () => {
      const r = await catalogApi.testChannel(c.id)
      setProbes((x) => ({ ...x, [c.id]: { ok: r.ok, status: r.status, error: r.error, latency_ms: r.latency_ms, models: r.models } }))
      if (r.ok) toast.ok(t(G.providers.testOk, { ms: r.latency_ms, n: r.models ?? '-' }))
      else toast.error(t(G.providers.testFail, { err: r.error ?? `HTTP ${r.status}` }))
    })

  const recover = (c: CatalogChannel) =>
    run('recover', async () => {
      const r = await catalogApi.resetChannelHealth(c.id)
      toast.ok(t(G.providers.recovered, { v: r.config_version }))
      reloadAll()
    })

  const fetchModels = (c: CatalogChannel) =>
    run('fetch', async () => {
      const r = await catalogApi.fetchChannelModels(c.id)
      setFetched((x) => ({ ...x, [c.id]: r.models }))
    })

  const patch = (c: CatalogChannel, body: Parameters<typeof catalogApi.patchChannel>[1], key: string, okText?: string) =>
    run(key, async () => {
      const r = await catalogApi.patchChannel(c.id, body)
      toast.ok(okText ?? t(G.providers.savedVersion, { v: r.config_version }))
      reloadAll()
    })

  const remove = async () => {
    if (!deleting) return
    setBusy('delete')
    setDelError('')
    try {
      await catalogApi.deleteChannel(deleting.id)
      toast.ok(t(G.providers.deleted))
      setDeleting(null)
      reloadAll()
    } catch (e) {
      console.error('[providers] 删除失败', e)
      setDelError(errorTextCat(e, locale))
    } finally {
      setBusy('')
    }
  }

  return (
    <div id="panel-providers" className="panel active" style={{ height: 'calc(100vh - 56px - 48px)', minHeight: 520, display: 'flex', flexDirection: 'column' }}>
      {!isAdmin && (
        <div style={{ marginBottom: 12 }}>
          <Notice tone="accent">{t(G.common.readOnly)}</Notice>
        </div>
      )}
      <div className="providers-layout" style={{ flex: 1 }}>
        <div className="providers-list-panel">
          <div className="providers-search">
            <SearchBox value={search} onChange={setSearch} placeholder={t(G.providers.search)} />
          </div>
          <div className="providers-list">
            {list.error ? (
              <div className="empty" style={{ padding: 16, color: 'var(--error-text)', fontSize: 12 }}>
                {t(G.common.loadFailed, { msg: errorTextCat(list.error, locale) })}
              </div>
            ) : list.loading && !list.data ? (
              <div className="empty" style={{ padding: 16, fontSize: 12 }}>
                {t(G.common.loading)}
              </div>
            ) : shown.length === 0 ? (
              <div className="empty" style={{ padding: 16, fontSize: 12, color: 'var(--text-tertiary)' }}>
                {t(G.providers.none)}
              </div>
            ) : (
              shown.map((c) => {
                const h = healthOf(c)
                return (
                  <div key={c.id} className={`provider-item${c.id === selected ? ' active' : ''}`} onClick={() => setSelected(c.id)}>
                    <Logo url={c.icon_url} name={c.name || c.id} />
                    <div className="provider-item-main">
                      <div className="provider-item-name">{c.name || c.id}</div>
                      <div className="provider-item-sub">
                        <span className={`health-dot ${h === 'off' ? '' : h}`} />
                        <span>{t(G.providers.healthState[h])}</span>
                        <span>·</span>
                        <span>{c.provider_type}</span>
                      </div>
                    </div>
                  </div>
                )
              })
            )}
          </div>
          {isAdmin && (
            <div className="providers-add">
              <button className="add-btn" onClick={() => setPicking(true)}>
                <IconPlus size={14} />
                {t(G.providers.add)}
              </button>
            </div>
          )}
        </div>

        <div className="detail-panel">
          {!current ? (
            <div className="detail-empty">
              <IconPlugConnected size={40} style={{ opacity: 0.3 }} />
              <p>{t(G.providers.pickOne)}</p>
            </div>
          ) : (
            <Detail
              key={current.id}
              c={current}
              stat={stats.data?.data[current.id]}
              hours={stats.data?.hours ?? 24}
              probe={probes[current.id]}
              fetched={fetched[current.id]}
              busy={busy}
              isAdmin={isAdmin}
              locale={locale}
              onTest={() => probe(current)}
              onRecover={() => recover(current)}
              onFetch={() => fetchModels(current)}
              onEdit={() => setEditing(current)}
              onDelete={() => {
                setDelError('')
                setDeleting(current)
              }}
              onPatch={(body, key, okText) => patch(current, body, key, okText)}
            />
          )}
        </div>
      </div>

      <PresetPicker
        open={picking}
        channelIds={channels.map((c) => c.id)}
        onClose={() => setPicking(false)}
        onCustom={() => {
          setPicking(false)
          setEditing('new')
        }}
        onInstalled={(r) => {
          setPicking(false)
          const head = r.models_endpoint
            ? t(G.presets.done, { name: r.name, n: r.models_registered, ms: r.latency_ms })
            : t(G.presets.doneNoList, { name: r.name })
          toast.ok(`${head} ${t(G.presets.publishNext)}`)
          pendingId.current = r.id
          setSelected(r.id)
          reloadAll()
        }}
      />
      <ChannelForm
        channel={editing}
        onClose={() => setEditing(null)}
        onSaved={() => {
          setEditing(null)
          reloadAll()
        }}
      />
      <ConfirmModal
        open={deleting !== null}
        danger
        busy={busy === 'delete'}
        title={t(G.providers.confirmDelete, { name: deleting?.name ?? '' })}
        confirmLabel={t(C.delete)}
        onConfirm={remove}
        onClose={() => setDeleting(null)}
      >
        <div className="ap-stack" style={{ gap: 8 }}>
          <span>{t(G.providers.deleteHint)}</span>
          {delError && <Notice tone="danger">{delError}</Notice>}
        </div>
      </ConfirmModal>
    </div>
  )
}

function Detail({
  c,
  stat,
  hours,
  probe,
  fetched,
  busy,
  isAdmin,
  locale,
  onTest,
  onRecover,
  onFetch,
  onEdit,
  onDelete,
  onPatch,
}: {
  c: CatalogChannel
  stat: ChannelStat | undefined
  hours: number
  probe: ProbeResult | undefined
  fetched: FetchedModel[] | undefined
  busy: string
  isAdmin: boolean
  locale: 'ko' | 'zh' | 'en'
  onTest: () => void
  onRecover: () => void
  onFetch: () => void
  onEdit: () => void
  onDelete: () => void
  onPatch: (body: Parameters<typeof catalogApi.patchChannel>[1], key: string, okText?: string) => void
}) {
  const { t } = useI18n()
  const h = healthOf(c)
  // 熔断策略草稿：切换渠道时由 key 重建组件，所以这里直接用渠道当前值初始化
  const [autoDisable, setAutoDisable] = useState(c.auto_disable)
  const [threshold, setThreshold] = useState(String(c.failure_threshold))
  const [cooldown, setCooldown] = useState(String(c.cooldown_seconds))
  const [apiKey, setApiKey] = useState('')
  const [iconUrl, setIconUrl] = useState(c.icon_url)
  const [mismatch, setMismatch] = useState(c)
  if (mismatch !== c) {
    setMismatch(c)
    setAutoDisable(c.auto_disable)
    setThreshold(String(c.failure_threshold))
    setCooldown(String(c.cooldown_seconds))
    setIconUrl(c.icon_url)
  }

  const requests = stat?.requests ?? 0
  const failed = requests - (stat?.ok ?? 0)
  const heroCopy =
    h === 'off'
      ? t(G.providers.copyOff)
      : h === 'open'
        ? t(G.providers.copyOpen, { n: c.consecutive_failures, until: c.auto_disabled_until ? fmtDateTime(c.auto_disabled_until, locale) : '—', reason: c.disabled_reason || '—' })
        : h === 'degraded'
          ? t(G.providers.copyDegraded, { n: c.consecutive_failures, max: c.failure_threshold })
          : requests === 0
            ? t(G.providers.copyIdle, { h: hours })
            : t(G.providers.copyHealthy, { h: hours, failed })

  const breakerDetail = !c.auto_disable
    ? t(G.providers.breakerManual)
    : h === 'open'
      ? t(G.providers.breakerOpen, { until: c.auto_disabled_until ? fmtDateTime(c.auto_disabled_until, locale) : '—' })
      : t(G.providers.breakerClosed, { n: c.consecutive_failures })

  const saveBreaker = () => {
    const th = Number(threshold)
    const cd = Number(cooldown)
    onPatch({ auto_disable: autoDisable, failure_threshold: th, cooldown_seconds: cd }, 'breaker', t(G.providers.breakerSaved))
  }

  const options = c.options
  const optionBadges = [
    options?.http2 && 'h2',
    options?.max_completion_tokens && 'max_completion_tokens',
    options?.inject_reasoning_content && 'reasoning_content',
    (options?.retry_statuses ?? []).length > 0 && `retry ${(options.retry_statuses ?? []).join('/')}`,
    options?.stream_usage && 'stream usage',
    options?.drop_prompt_cache_key && '-prompt_cache_key',
    ...(options?.drop_params ?? []).map((p) => `-${p}`),
  ].filter(Boolean) as string[]

  return (
    <div style={{ maxWidth: 1000 }}>
      <div className="detail-header">
        <div className="detail-header-left">
          <Logo url={c.icon_url} name={c.name || c.id} size="lg" />
          <div>
            <div className="detail-name">{c.name || c.id}</div>
            <div className="detail-url">
              {c.id} · {c.provider_type} · {c.base_url}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {probe && (
            <span className={`badge ${probe.ok ? 'badge-ok' : 'badge-off'}`} title={probe.error ?? ''}>
              {probe.ok ? t(G.providers.testOk, { ms: probe.latency_ms, n: probe.models ?? '-' }) : t(G.providers.testFail, { err: probe.error ?? `HTTP ${probe.status}` })}
            </span>
          )}
          {isAdmin && h !== 'healthy' && h !== 'off' && (
            <button className="btn btn-secondary btn-sm" disabled={busy === 'recover'} title={t(G.providers.recoverHint)} onClick={onRecover}>
              <IconHeartRateMonitor size={14} style={{ verticalAlign: -2, marginRight: 4 }} />
              {t(G.providers.recover)}
            </button>
          )}
          {isAdmin && (
            <button className="btn btn-secondary btn-sm" disabled={busy === 'test'} onClick={onTest}>
              <IconStethoscope size={14} style={{ verticalAlign: -2, marginRight: 4 }} />
              {busy === 'test' ? t(G.providers.testing) : t(G.providers.test)}
            </button>
          )}
          {isAdmin && (
            <button className="btn btn-secondary btn-sm" onClick={onEdit}>
              <IconSettings size={14} style={{ verticalAlign: -2, marginRight: 4 }} />
              {t(G.providers.fullSettings)}
            </button>
          )}
        </div>
      </div>

      <div className="provider-health-hero">
        <div className="health-hero-head">
          <div>
            <div className="health-state">
              <span className={`health-dot ${h === 'off' ? '' : h}`} />
              <span>{t(G.providers.healthState[h])}</span>
            </div>
            <div className="health-copy">{heroCopy}</div>
          </div>
          <span className="badge badge-info">{t(G.providers.window, { h: hours })}</span>
        </div>
        <div className="health-metrics">
          <Metric label={t(G.providers.mSuccess)} value={pct(stat?.ok ?? 0, requests)} />
          <Metric label={t(G.providers.mRequests)} value={requests.toLocaleString()} />
          <Metric label={t(G.providers.mP95)} value={fmtMs(stat?.p95_ms)} />
          <Metric label={t(G.providers.mFallback)} value={String(stat?.fallback_switches ?? 0)} />
          <Metric label={t(G.providers.mRoutes)} value={`${stat?.routes_active ?? 0} / ${stat?.routes_total ?? 0}`} />
        </div>
      </div>

      <div className="section config-card">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <div>
            <div className="section-title" style={{ marginBottom: 3 }}>
              {t(G.providers.breakerTitle)}
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{t(G.providers.breakerCopy)}</div>
          </div>
          <IosToggle on={autoDisable} onChange={setAutoDisable} disabled={!isAdmin} />
        </div>
        <div className="breaker-grid">
          <div className="mini-field">
            <label>{t(G.providers.breakerState)}</label>
            <div style={{ height: 34, display: 'flex', alignItems: 'center', fontSize: 12, color: 'var(--text-secondary)' }}>{breakerDetail}</div>
          </div>
          <div className="mini-field">
            <label>{t(G.providers.threshold)}</label>
            <input type="number" min={1} max={100} value={threshold} disabled={!isAdmin} onChange={(e) => setThreshold(e.target.value)} />
          </div>
          <div className="mini-field">
            <label>{t(G.providers.cooldown)}</label>
            <input type="number" min={0} max={86400} value={cooldown} disabled={!isAdmin} onChange={(e) => setCooldown(e.target.value)} />
          </div>
        </div>
        {isAdmin && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10 }}>
            <button className="btn btn-primary btn-sm" disabled={busy === 'breaker'} onClick={saveBreaker}>
              {t(G.providers.saveBreaker)}
            </button>
          </div>
        )}
      </div>

      <div className="provider-config-grid" style={{ marginBottom: 20 }}>
        <div className="config-card">
          <div className="section-title">{t(G.providers.limits)}</div>
          <div className="d-row">
            <span className="d-label">{t(G.providers.rpm)}</span>
            <span className="d-value">{c.rpm.toLocaleString()}</span>
          </div>
          <div className="d-row">
            <span className="d-label">{t(G.providers.concurrent)}</span>
            <span className="d-value">{c.max_concurrent.toLocaleString()}</span>
          </div>
          <div className="d-row">
            <span className="d-label">{t(G.providers.enabled)}</span>
            <IosToggle
              on={c.enabled}
              disabled={!isAdmin || busy === 'enabled'}
              onChange={(v) => onPatch({ enabled: v }, 'enabled', t(v ? G.providers.enabledOn : G.providers.enabledOff))}
            />
          </div>
          <div className="d-row">
            <span className="d-label">{t(G.providers.updatedAt)}</span>
            <span className="d-value">{fmtDateTime(c.updated_at, locale)}</span>
          </div>
        </div>
        <div className="config-card">
          <div className="section-title">{t(G.providers.options)}</div>
          {optionBadges.length ? (
            <div className="cat-chip-row">
              {optionBadges.map((b) => (
                <span key={b} className="route-chip mono">
                  {b}
                </span>
              ))}
            </div>
          ) : (
            <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{t(G.providers.noOptions)}</span>
          )}
          <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 10 }}>{t(G.providers.optionsHint)}</div>
        </div>
      </div>

      <div className="section">
        <div className="section-title">{t(G.providers.keyTitle)}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
          <code
            style={{ flex: 1, padding: '8px 12px', background: 'var(--bg-subtle)', border: '1px solid var(--border-medium)', borderRadius: 6, fontSize: 12, fontFamily: 'var(--mono)' }}
          >
            {c.api_key_hint}
          </code>
        </div>
        {isAdmin && (
          <>
            <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginBottom: 8 }}>{t(G.providers.keyHint)}</div>
            <form
              className="field-row"
              onSubmit={(e) => {
                e.preventDefault()
                if (!apiKey.trim()) return
                onPatch({ api_key: apiKey.trim() }, 'key', t(G.providers.keyRotated))
                setApiKey('')
              }}
            >
              <input
                className="form-input"
                type="text"
                autoComplete="off"
                spellCheck={false}
                minLength={8}
                maxLength={4096}
                placeholder={t(G.providers.keyPlaceholder)}
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                style={{ fontFamily: 'var(--mono)' }}
              />
              <button className="btn btn-primary btn-sm" type="submit" disabled={busy === 'key' || apiKey.trim().length < 8}>
                {t(G.providers.save)}
              </button>
            </form>
          </>
        )}
      </div>

      <div className="section">
        <div className="section-title">{t(G.providers.baseTitle)}</div>
        <input className="form-input" type="text" readOnly value={c.base_url} style={{ background: 'var(--bg-subtle)', color: 'var(--text-secondary)', width: '100%' }} />
      </div>

      <div className="section">
        <div className="section-title">Logo</div>
        <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginBottom: 8 }}>{t(G.providers.logoHint)}</div>
        <div className="field-row">
          <input
            className="form-input"
            type="text"
            placeholder="https://..."
            value={iconUrl}
            disabled={!isAdmin}
            onChange={(e) => setIconUrl(e.target.value)}
            style={{ fontFamily: 'var(--mono)' }}
          />
          {isAdmin && (
            <button className="btn btn-primary btn-sm" disabled={busy === 'icon' || iconUrl.trim() === c.icon_url} onClick={() => onPatch({ icon_url: iconUrl.trim() }, 'icon', t(G.providers.logoSaved))}>
              {t(G.providers.save)}
            </button>
          )}
        </div>
      </div>

      <div className="section">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <div className="section-title" style={{ marginBottom: 0 }}>
            {t(G.providers.modelsTitle)}{' '}
            <span style={{ fontWeight: 400, color: 'var(--text-tertiary)' }}>
              {fetched ? t(G.providers.modelsFetched, { n: fetched.length }) : t(G.providers.modelsRegistered, { n: stat?.models ?? 0 })}
            </span>
          </div>
          {isAdmin && (
            <button className="btn btn-secondary btn-sm" disabled={busy === 'fetch'} onClick={onFetch}>
              {busy === 'fetch' ? t(G.providers.fetching) : t(G.providers.fetchModels)}
            </button>
          )}
        </div>
        <div style={{ maxHeight: 300, overflowY: 'auto', border: '1px solid var(--border-medium)', borderRadius: 6, background: 'var(--bg-subtle)' }}>
          {!fetched ? (
            <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 13 }}>{t(G.providers.fetchEmpty)}</div>
          ) : fetched.length === 0 ? (
            <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 13 }}>{t(G.providers.fetchNone)}</div>
          ) : (
            fetched.map((m) => (
              <div key={m.model_id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '8px 12px', borderBottom: '1px solid var(--border)', fontSize: 12 }}>
                <span className="mono" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {m.model_id}
                </span>
                <span style={{ display: 'flex', gap: 8, alignItems: 'center', color: 'var(--text-tertiary)', flexShrink: 0 }}>
                  {m.context_length ? <span>{fmtContext(m.context_length)}</span> : null}
                  <span className={`badge ${m.registered ? 'badge-ok' : 'badge-info'}`}>{t(m.registered ? G.providers.registered : G.providers.unregistered)}</span>
                </span>
              </div>
            ))
          )}
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 6 }}>{t(G.providers.fetchFoot)}</div>
      </div>

      {isAdmin && (
        <div className="section" style={{ paddingTop: 16, borderTop: '1px solid var(--border)' }}>
          <button className="btn btn-danger btn-sm" onClick={onDelete}>
            <IconTrash size={14} style={{ verticalAlign: -2, marginRight: 4 }} />
            {t(G.providers.delete)}
          </button>
        </div>
      )}
    </div>
  )
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="health-metric">
      <div className="health-metric-label">{label}</div>
      <div className="health-metric-value">{value}</div>
    </div>
  )
}
